import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { getAuthToken } from '../../lib/authToken';

interface DocumentAccessHistoryProps {
  documentId: string;
}

const DocumentAccessHistory: React.FC<DocumentAccessHistoryProps> = ({ documentId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['document-access', documentId],
    queryFn: async () => {
      const res = await fetch(`/api/audit/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load access history');
      return json.data;
    },
    enabled: Boolean(documentId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    console.error('Document access history error:', error);
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Unable to load access history. Please try again.
      </Alert>
    );
  }

  const trail = data?.accessTrail || data?.auditLogs || [];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Access history
      </Typography>
      {trail.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No access events recorded for this document.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>User</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trail.map((row: any) => (
              <TableRow key={row.id || `${row.timestamp}-${row.action}`}>
                <TableCell>
                  {row.timestamp || row.createdAt
                    ? format(new Date(row.timestamp || row.createdAt), 'MMM d, yyyy HH:mm')
                    : '—'}
                </TableCell>
                <TableCell>{row.action}</TableCell>
                <TableCell>
                  {row.user?.name || row.user?.email || row.userId || '—'}
                </TableCell>
                <TableCell>{row.ipAddress || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status || 'ok'}
                    color={row.status === 'failed' || row.status === 'error' ? 'error' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default DocumentAccessHistory;
